import { disconnectSession, toggleStreamingState } from 'actions/opentok'
import { Flex } from 'components/styled/containers/flex'
import { Icon } from 'components/styled/icon'
import { LoadingIndicator } from 'components/styled/loading-indicator'
import config from 'config'
import { setError, updateError } from 'interview/actions/sequenced-interview'
import Timer, { blink } from 'interview/components/sequenced-interview/timer'
import { mapLanguageToComponentProps } from 'interview/i18n'
import PropTypes from 'prop-types'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import styled, { css } from 'styled-components'
import AudioMeter from './publisher-elements/audio-meter'
import Countdown from './publisher-elements/countdown'
import StreamButton from './publisher-elements/stream-button'
import StreamInfo from './publisher-elements/stream-info'

const propTypes = {
  i18n: PropTypes.shape({}).isRequired,
  dispatch: PropTypes.func.isRequired,
  testing: PropTypes.bool,
  publisher: PropTypes.shape({
    stream: PropTypes.shape({}),
    session: PropTypes.shape({}),
  }),
  session: PropTypes.shape({
    currentState: PropTypes.string,
  }),
  streaming: PropTypes.bool.isRequired,
  recording: PropTypes.bool.isRequired,
  reconnecting: PropTypes.bool.isRequired,
  timerCount: PropTypes.number,
  totalTime: PropTypes.number,
  answerCountdown: PropTypes.bool,
  toNextCountdown: PropTypes.bool,
  candidateError: PropTypes.string,
  opentokError: PropTypes.string,
  prep: PropTypes.bool,
  stopped: PropTypes.bool.isRequired,
  errorType: PropTypes.string,
  connectionCount: PropTypes.number,
  submitting: PropTypes.bool,
  onSubmit: PropTypes.func,
  onStop: PropTypes.func,
  lastQuestion: PropTypes.bool,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]),
  positionLanguage: PropTypes.string,
}

const defaultProps = {
  errorType: '',
  candidateError: '',
  opentokError: '',
  testing: false,
  publisher: undefined,
  session: undefined,
  timerCount: 0,
  totalTime: 0,
  answerCountdown: false,
  toNextCountdown: false,
  prep: false,
  connectionCount: 0,
  submitting: false,
  onSubmit: undefined,
  onStop: undefined,
  lastQuestion: false,
  children: undefined,
  positionLanguage: 'en',
}

const mapStateToProps = state => ({
  streaming: state.global.opentok.streaming,
  publisher: state.global.opentok.publisher,
  session: state.global.opentok.session,
  recording: state.global.opentok.recording,
  reconnecting: state.global.opentok.reconnecting,
  opentokError: state.global.opentok.error,
  connectionCount: state.global.opentok.connectionCount,
  candidateError: state.interview.candidate.error,
  stopped: state.interview.sequencedInterview.interviewState.stopped,
  toNextCountdown:
    state.interview.sequencedInterview.interviewState.toNextCountdown,
  answerCountdown:
    state.interview.sequencedInterview.interviewState.answerCountdown,
  errorType: state.interview.sequencedInterview.errorType,
  positionLanguage:
    state.interview &&
    state.interview.jobOpening &&
    state.interview.jobOpening.language,
})

const automaticArchiving =
  !config.DISABLE_SEQUENCED_INTERVIEW_AUTOMATIC_ARCHIVING

const DarkenedStyle = css`
  position: absolute;
  width: inherit;
  height: inherit;
  z-index: 1;
  top: 0;
  background-color: ${({ theme }) => theme.color.blackO30};
  padding-bottom: 75%;
`

const PublisherContainer = styled(Flex)`
  position: relative;
  width: 100%;
  height: 100%;
`

const StreamContainer = styled.div`
  display: ${props => (props.$show ? 'block' : 'none')};

  border-radius: ${({ theme }) => theme.space.s8};
  overflow: hidden;
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 75%;

  #publisher {
    height: inherit;
    width: inherit;
    position: relative;
    padding-bottom: 75%;

    .OT_publisher {
      width: inherit;
      height: inherit;
      padding-bottom: 75%;

      ${({ theme }) => theme.mediaQuery.landscapePhone} {
        width: 480px;
        height: 360px;
        padding-bottom: 0;
      }
    }

    ${({ theme }) => theme.mediaQuery.landscapePhone} {
      width: 480px;
      height: 360px;
      padding-bottom: 0;
    }

    ${({ theme }) => theme.mediaQuery.phone} {
      padding-bottom: 0;
    }
  }

  ${({ theme }) => theme.mediaQuery.phone} {
    width: 100vw;
    height: 100%;
    border-radius: 0;
    padding-bottom: 0;
  }

  ${({ theme }) => theme.mediaQuery.landscapePhone} {
    width: 480px;
    height: 360px;
    padding-bottom: 0;
  }
`

const RecordingIconContainer = styled.div`
  position: absolute;
  z-index: 2;
  top: ${({ theme }) => theme.space.s16};
  right: ${({ theme }) => theme.space.s20};
`

const StyledRecordingIcon = styled(Icon)`
  animation: ${blink} 2s ease-in-out infinite;
`

const DarkenedOverlay = styled.div`
  ${props =>
    props.$darkened &&
    DarkenedStyle}; // removed the fade from black from here, maybe we want it back?
`
export class Publisher extends Component {
  constructor(props) {
    super(props)

    this.state = {
      meterHeight: 0,
    }

    this.publisherRef = React.createRef()
  }

  componentDidUpdate(prevProps) {
    const { dispatch, testing, publisher, session, connectionCount } =
      this.props
    if (publisher && session && session.currentState === 'connected') {
      if (publisher !== prevProps.publisher) {
        if (!publisher.session && !publisher.stream) {
          this.publishToSession()
        }

        if (testing) {
          let audioLevelAverage = null
          publisher.on({
            audioLevelUpdated: event => {
              if (
                audioLevelAverage === null ||
                audioLevelAverage <= event.audioLevel
              ) {
                audioLevelAverage = event.audioLevel
              } else {
                audioLevelAverage =
                  0.7 * audioLevelAverage + 0.3 * event.audioLevel
              }

              let logLevel = Math.log(audioLevelAverage) / Math.LN10 / 1.5 + 1
              logLevel = Math.min(Math.max(logLevel, 0), 1)
              const percentage = logLevel * 100
              this.setState({ meterHeight: percentage })
            },
          })
        }

        publisher.on({
          streamCreated: event => {
            dispatch(toggleStreamingState(true))
            const publisherElement = this.publisherRef.current
            if (publisherElement) {
              publisherElement.scrollIntoView({ behavior: 'smooth' })
            }
          },
          streamDestroyed: event => {
            if (event.reason === 'networkDisconnected') {
              dispatch(updateError('networkError'))
            }
            dispatch(toggleStreamingState(false))
          },
        })
      } else if (
        publisher.session &&
        publisher.stream &&
        connectionCount !== prevProps.connectionCount &&
        connectionCount > 1 &&
        !testing
      ) {
        dispatch(disconnectSession())
        dispatch(updateError('tooManyConnections'))
      }
    }
  }

  getMinutesAndSeconds(time) {
    const seconds = `0${Math.floor((time / 1) % 60)}`.slice(-2)
    const minutes = Math.floor((time / 1 / 60) % 60)
    return `${minutes}:${seconds}`
  }

  publishToSession() {
    const { session, publisher, dispatch } = this.props
    session.publish(publisher, error => {
      if (error) {
        dispatch(setError(error))
      }
    })
  }

  renderPublisherElements() {
    const {
      i18n,
      prep,
      streaming,
      publisher,
      testing,
      recording,
      answerCountdown,
      toNextCountdown,
      totalTime,
      timerCount,
      candidateError,
      opentokError,
      stopped,
      reconnecting,
    } = this.props
    const { meterHeight } = this.state
    const hasError = candidateError || opentokError
    const timeLeft = totalTime - timerCount
    const countdownTimer = this.getMinutesAndSeconds(timeLeft)

    const countdownInfo =
      prep || answerCountdown
        ? i18n.sequencedInterview.publisher.recordingStarts(countdownTimer)
        : i18n.sequencedInterview.publisher.toNextQuestion(countdownTimer)

    if (hasError && !reconnecting) {
      return (
        <StreamInfo
          text={i18n.sequencedInterview.errors.generalError}
          color="error110"
        />
      )
    }

    if (streaming && publisher) {
      if (testing) {
        return <AudioMeter meterHeight={meterHeight} />
      }

      if (answerCountdown || toNextCountdown || prep) {
        return (
          <Countdown
            infoText={countdownInfo}
            timeLeft={timeLeft}
            toNextCountdown={toNextCountdown}
          />
        )
      }

      if (!stopped && (recording || !automaticArchiving)) {
        return <Timer timerCount={timerCount} totalTime={totalTime} />
      }
    }
    return null
  }

  render() {
    const {
      testing,
      streaming,
      errorType,
      session,
      recording,
      prep,
      reconnecting,
      candidateError,
      opentokError,
      submitting,
      onSubmit,
      onStop,
      lastQuestion,
      publisher,
      children,
      positionLanguage,
    } = this.props
    const hasError = !!(errorType || candidateError || opentokError)

    return (
      <PublisherContainer
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        {!streaming && !hasError && !testing && <LoadingIndicator />}
        <StreamContainer
          $show={session && session.currentState === 'connected' && !errorType}
        >
          <div id="publisher" ref={this.publisherRef} />
          {this.renderPublisherElements()}
          <DarkenedOverlay
            $darkened={(!recording && !testing && !prep) || prep}
          />
          {recording && !reconnecting && (
            <RecordingIconContainer>
              <StyledRecordingIcon
                type="radio_button_checked"
                color="primary110"
              />
            </RecordingIconContainer>
          )}
          {children}
        </StreamContainer>
        {!testing && !hasError && publisher && (
          <StreamButton
            submitting={submitting}
            lastQuestion={lastQuestion}
            onSubmit={onSubmit}
            onStop={() => onStop(true)}
            flippedView={positionLanguage === 'ar'}
          />
        )}
      </PublisherContainer>
    )
  }
}

Publisher.propTypes = propTypes
Publisher.defaultProps = defaultProps

export default connect(mapLanguageToComponentProps(mapStateToProps))(Publisher)
